<template>
  <div class="container my-4">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h1 class="mb-0">Gestión de {{ nombreTipoPlural }}</h1>

      <div class="d-flex gap-2">
        <button
          class="btn"
          :class="massMode ? 'btn-outline-secondary' : 'btn-outline-primary'"
          @click="toggleMassMode"
        >
          {{ massMode ? "Cancelar" : "Movimiento masivo" }}
        </button>

        <button
          v-if="massMode"
          class="btn btn-primary"
          :disabled="selectedIds.length === 0"
          @click="abrirModalMasivo"
        >
          Generar movimiento ({{ selectedIds.length }})
        </button>

        <router-link :to="rutaNuevaPersona" class="btn btn-success ms-1">
          <FontAwesomeIcon icon="plus" />
          Nuevo
        </router-link>
      </div>
    </div>

    <!-- Buscador -->
    <div class="row g-2 align-items-center mb-3">
      <div class="col-md">
        <input
          v-model="busqueda"
          class="form-control"
          placeholder="Buscar por razón social, nombre, apellido, CUIT o DNI..."
        />
      </div>
      <div class="col-md-auto">
        <div class="form-check">
          <input
            id="solo-activos"
            v-model="soloActivos"
            class="form-check-input"
            type="checkbox"
            @change="cargarClientes"
          />
          <label class="form-check-label" for="solo-activos">Activos</label>
        </div>
      </div>
    </div>

    <!-- Tabla -->
    <table class="table table-bordered table-striped align-middle">
      <thead>
        <tr>
          <th v-if="massMode" style="width: 42px">
            <input
              type="checkbox"
              class="form-check-input"
              :checked="todosSeleccionados"
              :indeterminate.prop="indeterminado"
              @change="toggleSeleccionTodos"
              title="Seleccionar todos"
            />
          </th>
          <th>Nombre</th>
          <th>Tipo</th>
          <th>CUIT</th>
          <th>Teléfono</th>
          <th>Lista</th>
          <th>Saldo</th>
          <th>Acciones</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="c in clientesFiltrados" :key="c._id">
          <td v-if="massMode" class="text-center">
            <input
              type="checkbox"
              class="form-check-input"
              :value="c._id"
              v-model="selectedIds"
            />
          </td>
          <td>
            {{ nombreCliente(c) }}
            <span v-if="c.activo === false" class="badge text-bg-secondary ms-2">
              Inactivo
            </span>
          </td>
          <td>
            <span v-if="c.es_cliente !== false" class="badge text-bg-primary me-1">Cliente</span>
            <span v-if="c.es_proveedor !== false" class="badge text-bg-info">Proveedor</span>
          </td>
          <td>{{ c.cuit_cuil }}</td>
          <td>{{ c.telefono1 }}</td>
          <td>{{ c.lista_precio_nombre || "—" }}</td>
          <td>{{ formatearMoneda(c.saldo || 0) }}</td>

          <td>
            <div class="btn-group d-flex flex-wrap gap-1">
              <router-link
                :to="{ path: `/clientes/${c._id}/editar`, query: contextoEdicionQuery }"
                class="btn btn-sm btn-outline-primary"
              >
                <FontAwesomeIcon icon="pen-to-square" />
                Editar
              </router-link>
            </div>
          </td>
        </tr>
        <tr v-if="clientesFiltrados.length === 0">
          <td :colspan="massMode ? 8 : 7" class="text-center">
            No se encontraron {{ nombreTipoPlural.toLowerCase() }}.
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Modal Movimiento Masivo -->
    <div
      v-if="showModal"
      class="modal modal-custom fade show"
      style="display: block"
      tabindex="-1"
      aria-modal="true"
      role="dialog"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <form @submit.prevent="confirmarMovimientoMasivo">
            <div class="modal-header">
              <h5 class="modal-title">Generar movimiento masivo</h5>
              <button
                type="button"
                class="btn-close"
                @click="cerrarModal"
                :disabled="submitting"
              ></button>
            </div>
            <div class="modal-body">
              <div class="alert alert-info py-2">
                Se aplicará a
                <strong>{{ selectedIds.length }}</strong> {{ nombreTipoPlural.toLowerCase() }}.
              </div>

              <div class="mb-3">
                <label class="form-label">Fecha</label>
                <input
                  v-model="form.fecha"
                  type="date"
                  class="form-control"
                  required
                />
              </div>

              <div class="mb-3">
                <label class="form-label">Tipo</label>
                <select
                  v-model="form.tipo_id"
                  class="form-select"
                  required
                  @change="onTipoChange"
                >
                  <option disabled value="">Seleccionar tipo</option>
                  <option
                    v-for="t in tiposMovimiento"
                    :key="t._id"
                    :value="t._id"
                  >
                    {{ t.nombre }} ({{ t.signo }})
                  </option>
                </select>
                <div v-if="tipoSeleccionado?.descripcion" class="form-text">
                  {{ tipoSeleccionado.descripcion }}
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Detalle</label>
                <input
                  v-model.trim="form.detalle"
                  type="text"
                  class="form-control"
                  minlength="3"
                  required
                  placeholder="Ej: Mantenimiento mensual"
                />
              </div>

              <div class="mb-1">
                <label class="form-label">Importe</label>
                <input
                  id="importe-masivo"
                  v-model="form.importe" v-importe
                  type="number"
                  step="0.01"
                  min="0.01"
                  class="form-control"
                  required
                  placeholder="0,00"
                />
              </div>
              <div class="form-text">
                Signo: <strong>{{ tipoSeleccionado?.signo || "-" }}</strong>
              </div>
            </div>
            <div class="modal-footer">
              <button
                type="button"
                class="btn btn-outline-secondary"
                @click="cerrarModal"
                :disabled="submitting"
              >
                Cancelar
              </button>
              <button
                type="submit"
                class="btn btn-primary"
                :disabled="!formValido || submitting"
              >
                <span
                  v-if="submitting"
                  class="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Confirmar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    <!-- Backdrop propio -->
    <div v-if="showModal" class="modal-backdrop-custom fade show"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch, nextTick } from "vue";
import { useRoute } from "vue-router";
import api from "@/api";
import { showAlert } from "@/utils/dialogs";

interface Cliente {
  _id?: string;
  razon_social?: string | null;
  nombre?: string;
  nombre_display?: string;
  nombres: string | null;
  apellido: string | null;
  dni: string | null;
  cuit_cuil: string;
  nro_iibb: string | null;
  saldo: number;
  situacion_iva: string | null;
  direccion: string | null;
  localidad: string | null;
  telefono1: string | null;
  telefono2: string | null;
  email: string | null;
  grupo: string | null;
  lista_precio_id?: string;
  lista_precio_nombre?: string;
  activo?: boolean;
  es_cliente?: boolean;
  es_proveedor?: boolean;
}

interface MovimientoTipo {
  _id: string;
  nombre: string;
  signo: "debe" | "haber";
  interes_punitorio?: number;
  vencimiento_dias?: number;
  descripcion?: string;
}

const clientes = ref<Cliente[]>([]);
const route = useRoute();
const esListadoProveedores = computed(() => route.path === "/proveedores");
const tipoListado = computed(() => esListadoProveedores.value ? "proveedor" : "cliente");
const nombreTipoPlural = computed(() => esListadoProveedores.value ? "Proveedores" : "Clientes");
const nombreTipoSingular = computed(() => esListadoProveedores.value ? "proveedor" : "cliente");
const contextoEdicionQuery = computed(() => esListadoProveedores.value ? { tipo: "proveedor" } : {});
const rutaNuevaPersona = computed(() => ({
  path: "/clientes/nuevo",
  query: contextoEdicionQuery.value,
}));
const busqueda = ref("");
const soloActivos = ref(true);

/** ───── Movimiento masivo state ───── */
const massMode = ref(false);
const selectedIds = ref<string[]>([]);
const showModal = ref(false);
const submitting = ref(false);

const tiposMovimiento = ref<MovimientoTipo[]>([]);
const tipoSeleccionado = computed(
  () => tiposMovimiento.value.find((t) => t._id === form.value.tipo_id) || null
);

const today = () => new Date().toISOString().slice(0, 10);
const form = ref({
  fecha: today(),
  tipo_id: "",
  detalle: "",
  importe: "" as string | number,
});

const nombreCliente = (cliente: Cliente) =>
  cliente.nombre_display ||
  cliente.nombre ||
  cliente.razon_social ||
  [cliente.apellido, cliente.nombres].filter(Boolean).join(" ").trim() ||
  "Sin nombre";

/** ───── Filtros y tabla ───── */
const clientesFiltrados = computed(() => {
  const list = !busqueda.value
    ? clientes.value
    : clientes.value.filter((c) =>
        [c.razon_social, c.nombres, c.apellido, c.cuit_cuil, c.dni, nombreCliente(c)].some((campo: any) =>
          (campo || "").toLowerCase().includes(busqueda.value.toLowerCase())
        )
      );
  return list.sort((a, b) => {
    const nombreA = nombreCliente(a).toLowerCase();
    const nombreB = nombreCliente(b).toLowerCase();
    return nombreA.localeCompare(nombreB);
  });
});

const todosSeleccionados = computed(
  () =>
    clientesFiltrados.value.length > 0 &&
    selectedIds.value.length === clientesFiltrados.value.length
);
const indeterminado = computed(
  () =>
    selectedIds.value.length > 0 &&
    selectedIds.value.length < clientesFiltrados.value.length
);

function toggleSeleccionTodos(e: Event) {
  const checked = (e.target as HTMLInputElement).checked;
  selectedIds.value = checked ? clientesFiltrados.value.map((c) => c._id!) : [];
}

function toggleMassMode() {
  massMode.value = !massMode.value;
  if (!massMode.value) selectedIds.value = [];
}

async function abrirModalMasivo() {
  if (selectedIds.value.length === 0) {
    await showAlert(`Seleccioná al menos un ${nombreTipoSingular.value}.`, {
      title: nombreTipoPlural.value,
      variant: "warning",
    });
    return;
  }
  // cargar tipos si hace falta
  if (!tiposMovimiento.value.length) {
    await cargarTiposMovimiento();
  }
  // preseleccionar primer tipo
  if (tiposMovimiento.value.length && !form.value.tipo_id) {
    form.value.tipo_id = tiposMovimiento.value[0]._id;
    onTipoChange();
  }
  showModal.value = true;
  await nextTick();
  setTimeout(() => {
    const el = document.getElementById(
      "importe-masivo"
    ) as HTMLInputElement | null;
    el?.focus();
  }, 30);
}

function cerrarModal() {
  if (submitting.value) return;
  showModal.value = false;
  // mantenemos selección para repetir con el mismo set si se desea
}

const formValido = computed(() => {
  const n = Number(form.value.importe);
  return (
    !!form.value.fecha &&
    !!form.value.tipo_id &&
    form.value.detalle.trim().length >= 3 &&
    !isNaN(n) &&
    n > 0
  );
});

function onTipoChange() {
  const t = tipoSeleccionado.value;
  if (t && !form.value.detalle.trim()) {
    form.value.detalle = t.nombre;
  }
}

/** ───── API calls ───── */
const cargarClientes = async () => {
  try {
    const res = await api.get<Cliente[]>("/clientes", {
      params: { activos: soloActivos.value, tipo: tipoListado.value },
    });
    clientes.value = res.data.sort((a, b) => {
      const nombreA = nombreCliente(a).toLowerCase();
      const nombreB = nombreCliente(b).toLowerCase();
      return nombreA.localeCompare(nombreB);
    });
    selectedIds.value = selectedIds.value.filter((id) =>
      clientes.value.some((cliente) => cliente._id === id)
    );
  } catch (error) {
    console.error("Error cargando clientes:", error);
  }
};

const cargarTiposMovimiento = async () => {
  try {
    const { data } = await api.get<MovimientoTipo[]>("/movimiento-tipos");
    tiposMovimiento.value = data;
  } catch (e) {
    console.error("Error cargando tipos de movimiento:", e);
  }
};

const confirmarMovimientoMasivo = async () => {
  if (!formValido.value || !tipoSeleccionado.value) return;
  try {
    submitting.value = true;
    const payload = {
      cliente_ids: selectedIds.value,
      descripcion: form.value.detalle.trim(),
      importe: Number(form.value.importe),
      tipo_id: tipoSeleccionado.value._id,
      tipo: tipoSeleccionado.value.nombre,
      fecha: form.value.fecha,
    };
    const { data } = await api.post(
      "/cuenta_corriente/movimientos/masivo",
      payload
    );
    await showAlert(
      `Se insertaron ${data.insertados} movimientos (${data.signo}) por ${data.importe}.`,
      {
        title: nombreTipoPlural.value,
        variant: "success",
      }
    );
    showModal.value = false;
    massMode.value = false;
    selectedIds.value = [];
    form.value = { fecha: today(), tipo_id: "", detalle: "", importe: "" };
  } catch (e: any) {
    const msg =
      e?.response?.data?.detail ||
      e.message ||
      "No se pudo generar el movimiento masivo";
    await showAlert(msg, {
      title: nombreTipoPlural.value,
      variant: "danger",
    });
    console.error(e);
  } finally {
    submitting.value = false;
  }
};

/** ───── Utils ───── */
const formatearMoneda = (valor: number) =>
  valor.toLocaleString("es-AR", { style: "currency", currency: "ARS" });

/** ───── Body scroll lock para el modal ───── */
watch(showModal, (val) => {
  const cls = "modal-open";
  if (val) document.body.classList.add(cls);
  else document.body.classList.remove(cls);
});

/** ───── Lifecycle ───── */
onMounted(() => {
  cargarClientes();
  cargarTiposMovimiento();
});

watch(tipoListado, () => {
  selectedIds.value = [];
  massMode.value = false;
  cargarClientes();
});
</script>

<style scoped>
/* Checkbox "indeterminate" visual */
input[type="checkbox"][indeterminate] {
  box-shadow: inset 0 0 0 10px #fff;
}

/* Modal custom: posicionamiento y layering correctos */
.modal-custom {
  position: fixed;
  inset: 0; /* top/right/bottom/left: 0 */
  display: block !important;
  z-index: 1065; /* por encima del backdrop */
  overflow: auto;
}

.modal-custom .modal-dialog {
  margin: 1.75rem auto;
}

/* Backdrop custom */
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1060; /* por debajo del modal */
}

/* Bloqueo de scroll cuando se abre el modal */
:global(body.modal-open) {
  overflow: hidden;
}
</style>
